import { config } from '../config.js';
import { authorizationHeader, redirectToLogin } from '../auth/authClient.js';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export type ApiService = 'tenant' | 'flow' | 'reporting';

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Override base URL (testing). */
  baseUrl?: string;
  /**
   * #155: target service. Default 'tenant' (tenant-mgmt HTTP API).
   * 'flow' → flow-engine, 'reporting' → reporting-api.
   */
  service?: ApiService;
  /** Extra headers (e.g. If-Match for optimistic concurrency). */
  headers?: Record<string, string>;
}

function resolveBaseUrl(service?: ApiService): string {
  switch (service) {
    case 'flow':
      return config.apiBaseUrlFlow;
    case 'reporting':
      return config.apiBaseUrlReporting;
    default:
      return config.apiBaseUrl;
  }
}

/**
 * Fetch wrapper:
 *  - Adds Authorization: Bearer <idToken>
 *  - JSON request/response
 *  - Throws ApiError on non-2xx; redirects to login on 401
 */
export async function apiFetch<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  const url = `${opts.baseUrl ?? resolveBaseUrl(opts.service)}${path}`;
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...(opts.headers ?? {}),
  };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  const authHeader = authorizationHeader();
  if (authHeader) headers.authorization = authHeader;

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'omit',
  });

  if (res.status === 401) {
    redirectToLogin();
    throw new ApiError(401, 'UNAUTHENTICATED', 'Sesión expirada');
  }

  const text = await res.text();
  const data: unknown = text ? safeParse(text) : null;

  if (!res.ok) {
    const obj = (data ?? {}) as { code?: string; message?: string };
    throw new ApiError(
      res.status,
      obj.code ?? `HTTP_${res.status}`,
      obj.message ?? res.statusText,
      data,
    );
  }
  return data as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
